/**
 * 청구 유형별 필요 서류 안내 (고객지원도구 · 청구 서류 체크)
 * Flutter `claim_required_documents_guide.dart`와 문구를 동기화할 것.
 *
 * 아래 내용은 종합보험·운전자·자동차·재물·배상 등 안내문에 흔히 실리는 서류 예시를
 * 바탕으로 정리한 참고 자료입니다. 가입 상품·보험사·사고 내용에 따라 달라지므로
 * 최종 제출 서류는 보험사·담당자 안내를 따르세요.
 */

/** 청구 유형 선택 전 기본(일반) — 공통·자주 쓰는 서류 */
export const CLAIM_GENERAL_DOCUMENTS_SECTION = {
  sectionTitle: '일반',
  panels: [
    {
      title: '참고 안내',
      defaultOpen: true,
      blocks: [
        {
          strong: '이 목록의 한계',
          lines: [
            '사고 내용, 특성, 상품(보장내역)에 따라 추가 심사 서류를 요구할 수 있습니다.',
            '서류는 다른 서류로 대체될 수 있으니, 반드시 담당자와 필요 서류를 상의하세요.',
          ],
        },
      ],
    },
    {
      title: '청구 시 자주 준비하는 공통 서류',
      defaultOpen: true,
      blocks: [
        {
          strong: '기본',
          lines: [
            '- 보험금청구서(계좌번호 포함)',
            '- 개인(신용)정보처리동의서',
            '- 청구인 신분증 사본',
          ],
        },
        {
          strong: '가족관계 확인이 필요한 경우',
          lines: [
            '배우자·자녀 등을 보장하는 상품, 수익자가 미성년자인 경우 등',
            '- 가족관계증명서, 혼인관계증명서, 주민등록등본, 의료보험카드 사본 등',
          ],
        },
        {
          strong: '대리인 청구 시',
          lines: [
            '- 위임장, 보험금 청구권자의 인감증명서(또는 본인서명사실확인서)',
            '- 보험금 청구권자의 개인(신용)정보처리동의서',
          ],
        },
        {
          strong: '상해사고 청구 시',
          lines: ['- 사고 입증 서류(「상해사고 입증서류」 유형에서 예시 확인)'],
        },
        {
          strong: '진단서·확인서류 공통',
          lines: [
            '진단서, 통원확인서, 처방전, 진료확인서, 소견서, 수술확인서, 진료차트 등에는 진단명이 기재되어 있어야 합니다.',
          ],
        },
        {
          strong: '의료비 첨부 시 자주 요청되는 서류',
          lines: [
            '- 진료비계산서(영수증), 진료비세부(상세)내역서',
            '- 처방전·약제비 영수증, 입퇴원확인서, 수술확인서(해당 시)',
          ],
        },
      ],
    },
  ],
}

export const CLAIM_REQUIRED_DOCUMENTS_GUIDE = [
  {
    sectionTitle: '실비보험',
    panels: [
      {
        title: '공통',
        defaultOpen: true,
        blocks: [
          {
            strong: '입·통원 진료비',
            lines: [
              '진료비계산서(영수증) — 카드 전표·소득공제용 「진료비납입확인서」 등은 보험사가 불가로 안내하는 경우가 많습니다.',
            ],
          },
          {
            strong: '비급여',
            lines: ['비급여가 있으면 진료비세부(상세)내역서가 필요한 경우가 많습니다.'],
          },
        ],
      },
      {
        title: '입원 실손 의료비',
        defaultOpen: true,
        blocks: [
          { strong: '진단서', lines: [] },
          {
            strong: '50만원 이하 청구 시 진단서 대체 가능(예시)',
            lines: [
              '진단명이 포함된 입퇴원확인서, 또는 진단명 및 입원기간이 포함된 진료확인서(보험사 기준)',
            ],
          },
          { strong: '진료비계산서(영수증)', lines: [] },
          {
            strong: '진료비세부(상세)내역서',
            lines: ['비급여가 있는 경우 등'],
          },
        ],
      },
      {
        title: '입원 일당',
        defaultOpen: false,
        blocks: [
          {
            strong: '진단명(질병분류코드)·입원기간이 포함된 서류',
            lines: ['예: 입퇴원확인서, 진단서 등'],
          },
        ],
      },
      {
        title: '통원·외래 의료비 — 10만원 초과',
        defaultOpen: false,
        blocks: [
          { strong: '진료비계산영수증', lines: [] },
          {
            strong: '진료비세부내역서',
            lines: ['비급여가 없는 경우 생략 가능한 예도 있음(보험사 기준)'],
          },
          {
            strong: '진단명(질병분류코드)이 포함된 서류',
            lines: [
              '예: 진단서·통원확인서·처방전·진료확인서·소견서·진료차트 등',
            ],
          },
        ],
      },
      {
        title: '통원·외래 의료비 — 3만원 초과 ~ 10만원 이하',
        defaultOpen: false,
        blocks: [
          { strong: '진료비계산영수증', lines: [] },
          {
            strong: '진료비세부내역서',
            lines: ['비급여가 없는 경우 생략 가능한 예도 있음'],
          },
          {
            strong: '(질병분류코드가 기재된) 처방전',
            lines: [
              '특정 진료과목(산부인과, 항문외과, 비뇨기과, 피부과 등)이거나 짧은 기간 내 청구 횟수가 많은 경우 등 추가 심사·추가 증빙이 필요할 수 있음',
            ],
          },
        ],
      },
      {
        title: '통원·외래 의료비 — 3만원 이하',
        defaultOpen: false,
        blocks: [
          {
            strong: '진료비계산영수증, 진료비세부내역서',
            lines: ['비급여가 없는 경우 생략 가능한 예도 있음'],
          },
          {
            strong: '추가 심사 가능 사유',
            lines: [
              '특정 진료과목·청구 횟수 과다 등 — 별도 추가 증빙을 요구할 수 있음',
            ],
          },
        ],
      },
      {
        title: '약제 의료비',
        defaultOpen: false,
        blocks: [
          { strong: '처방전', lines: [] },
          { strong: '약제비계산서(영수증)', lines: [] },
        ],
      },
    ],
  },
  {
    sectionTitle: '진단금',
    panels: [
      {
        title: '공통',
        defaultOpen: true,
        blocks: [{ strong: '진단서', lines: ['(진단명, 질병분류코드 포함)'] }],
      },
      {
        title: '암',
        defaultOpen: false,
        blocks: [
          { strong: '진단서', lines: [] },
          { strong: '조직검사 결과지', lines: [] },
          {
            strong: '질병별 예시',
            lines: [
              '· 백혈병: 골수검사지 및 혈액검사 결과지',
              '· 뇌/폐/췌장암: 방사선 판독결과지(조직검사 못할 경우)',
              '· 간: 방사선 판독결과지(조직검사 못할 경우) 및 혈액검사 결과지',
            ],
          },
        ],
      },
      {
        title: '심질환',
        defaultOpen: false,
        blocks: [
          { strong: '진단서', lines: [] },
          {
            strong: '각종 검사결과지',
            lines: [
              '예: 관상동맥 조영술, 심전도, 심초음파, 심장효소 혈액검사결과지 등',
            ],
          },
        ],
      },
      {
        title: '뇌질환',
        defaultOpen: false,
        blocks: [
          { strong: '진단서', lines: [] },
          { strong: 'CT, MRI 등 방사선 판독결과지', lines: [] },
        ],
      },
      {
        title: '기타 진단',
        defaultOpen: false,
        blocks: [
          { strong: '진단서', lines: [] },
          { strong: '진단사실 확인 서류', lines: [] },
        ],
      },
    ],
  },
  {
    sectionTitle: '골절, 수술, 깁스치료, 치아보험',
    panels: [
      {
        title: '골절',
        defaultOpen: false,
        blocks: [
          {
            strong: '진단명(질병분류코드)·진단일자가 포함된 서류',
            lines: [
              '예: 통원확인서·입퇴원확인서·X-ray 결과지, 진단서, 의사소견서 등',
            ],
          },
        ],
      },
      {
        title: '수술',
        defaultOpen: false,
        blocks: [
          {
            strong: '진단명(질병분류코드)·수술명·수술일자가 포함된 서류',
            lines: ['예: 수술확인서, 수술기록지, 진단서 등'],
          },
        ],
      },
      {
        title: '깁스치료',
        defaultOpen: false,
        blocks: [
          { strong: '통깁스 시행 여부 확인 가능한 진단서', lines: [] },
          {
            strong: '진료차트 및 진료비세부산정내역서',
            lines: ['(통깁스 시행일)'],
          },
        ],
      },
      {
        title: '영구치 발거 치료비',
        defaultOpen: false,
        blocks: [
          {
            strong: '치과치료 진료확인서(아래 반드시 포함)',
            lines: [
              '․ 발거한 영구치의 위치 또는 치아번호',
              '․ 해당 영구치 내원 당시 치아상태',
              '․ 직접적인 영구치 발거 원인',
            ],
          },
          {
            strong: '추가로 요구되는 경우가 많음',
            lines: [
              '치과진료기록 사본, 치료 전후 X-ray 또는 이에 준하는 판독자료',
            ],
          },
        ],
      },
      {
        title: '영구치 보존 치료비',
        defaultOpen: false,
        blocks: [
          {
            strong: '치과치료 진료확인서(아래 반드시 포함)',
            lines: [
              '․ 치료한 치아의 위치 또는 치아번호',
              '․ 해당 치아 내원 당시 상태',
              '․ 직접적인 치아치료 원인',
              '․ 진단확정일, 진료시작일, 진료종료일, 진료일수',
            ],
          },
          {
            strong: '추가',
            lines: ['치과진료기록 사본, 치료 전후 X-ray 등(필요 시)'],
          },
        ],
      },
      {
        title: '영구치 보철 치료비',
        defaultOpen: false,
        blocks: [
          {
            strong: '치과치료 진단서(아래 반드시 포함)',
            lines: [
              '․ 발거한 영구치의 위치 또는 치아번호',
              '․ 해당 영구치 내원 당시 치아상태',
              '․ 직접적인 영구치 발거 원인',
              '․ 진단확정일, 진료시작일, 진료종료일, 진료일수',
            ],
          },
          {
            strong: '추가',
            lines: ['치과진료기록 사본, 치료 전후 X-ray 등(필요 시)'],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '상해사고 입증서류',
    panels: [
      {
        title: '교통사고',
        defaultOpen: true,
        blocks: [
          {
            strong: '사고사실확인서',
            lines: [
              '공공기관(경찰서, 소방서 등), 손해보험사, 공제조합(버스·화물·택시 등) 발급분 등',
            ],
          },
        ],
      },
      {
        title: '산재사고',
        defaultOpen: false,
        blocks: [
          {
            strong: '요양급여신청서 또는 보험급여지급확인원',
            lines: ['근로복지공단'],
          },
        ],
      },
      {
        title: '군 복무 중 사고',
        defaultOpen: false,
        blocks: [{ strong: '공무상병인증서', lines: ['군부대'] }],
      },
      {
        title: '의료사고·법원 분쟁',
        defaultOpen: false,
        blocks: [{ strong: '법원 판결문', lines: ['법원'] }],
      },
      {
        title: '기타 상해·발급 불가 시',
        defaultOpen: false,
        blocks: [
          {
            strong: '공공기관 사고사실확인서',
            lines: ['경찰서, 소방서 등'],
          },
          {
            strong: '확인서류 발급이 어려울 때(예시)',
            lines: [
              '병원 초진차트 등 상해사고 증명 서류',
              '보험금청구서상 사고내용 기재(6하원칙에 따라 상세 기재) 등 — 보험사·담당자와 상의',
            ],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '태아보험',
    panels: [
      {
        title: '태아·신생아 입원비',
        defaultOpen: true,
        blocks: [
          {
            strong: '출생증명서 또는 가족관계증명서',
            lines: [],
          },
          {
            strong: '진단서',
            lines: [
              '50만원 이하 청구 시: 진단명이 포함된 입퇴원확인서, 또는 진단명·입원기간이 포함된 진료확인서로 대체 가능한 경우가 있음',
            ],
          },
          {
            strong: '입퇴원확인서',
            lines: [
              '인큐베이터 사용 시 해당 기간 명시(진단서에 입원·인큐베이터 기간이 포함된 경우는 제외되는 예도 있음)',
            ],
          },
        ],
      },
      {
        title: '유산/사산',
        defaultOpen: false,
        blocks: [
          {
            strong: '유산: 진단서(유산) / 사산: 사산증명서',
            lines: [],
          },
        ],
      },
      {
        title: '응급비용(해당 특약)',
        defaultOpen: false,
        blocks: [
          {
            strong: '119 또는 129 구급구조증명서',
            lines: ['소방서·구급대'],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '장해',
    panels: [
      {
        title: '장해',
        defaultOpen: false,
        blocks: [
          {
            strong: '후유장해진단서',
            lines: [
              '※ 발급 전 보험회사 콜센터 또는 지급담당자와 상의하는 것이 안전합니다.',
            ],
          },
          {
            strong: '(일반)진단서로 대체 가능한 장해 예시',
            lines: [
              '· 만성신부전: 혈액투석(최초투석일, 환자상태 기재)',
              '· 사지절단(절단부위 명시): X-ray 결과지',
              '· 인공관절치환술(치환일자·부위 명시): 수술기록지',
              '· 비장·신장적출(적출일자·부위 명시): 수술기록지',
              '· 장기전절제(절제일자·부위명시): 수술기록지',
            ],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '사망',
    panels: [
      {
        title: '사망',
        defaultOpen: false,
        blocks: [
          {
            strong: '사망진단서(사체검안서)',
            lines: [
              '원본 또는, 피보험자 기본증명서(사망사실 기재)가 첨부된 사망진단서(사체검안서) 사본(원본대조필 포함) 등',
            ],
          },
          {
            strong: '가족관계증명서',
            lines: ['사례별로 다르므로 사전 문의 권장'],
          },
          {
            strong: '수익자 미지정 시',
            lines: ['상속관계 확인 서류(가족관계증명서, 혼인관계증명서 등)'],
          },
          {
            strong: '1인의 상속인이 전액 수령을 원하는 경우',
            lines: [
              '상속인 각각의 위임장, 인감도장(또는 위임장에 인감도장 날인)',
              '인감증명서(또는 본인서명사실확인서)',
            ],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '운전자보험',
    panels: [
      {
        title: '공통·청구 기본',
        defaultOpen: true,
        blocks: [
          {
            strong: '보험금청구서(계좌번호 포함)',
            lines: [],
          },
          { strong: '개인(신용)정보처리동의서', lines: [] },
          { strong: '청구인 신분증 사본', lines: [] },
          {
            strong: '상해·교통 관련 담보',
            lines: ['사고 입증 서류(교통사고사실확인원 등 해당 시)'],
          },
          {
            strong: '대리인 청구 시',
            lines: [
              '위임장, 청구권자 인감증명서(또는 본인서명사실확인서), 청구권자 개인(신용)정보처리동의서',
            ],
          },
        ],
      },
      {
        title: '긴급비용',
        defaultOpen: false,
        blocks: [
          { strong: '견인비 영수증', lines: [] },
          { strong: '수리비 견적서', lines: ['수리업체'] },
        ],
      },
      {
        title: '벌금',
        defaultOpen: false,
        blocks: [
          { strong: '벌금영수증', lines: [] },
          { strong: '법원 판결문 또는 약식명령문', lines: ['법원'] },
        ],
      },
      {
        title: '자동차사고 변호사 선임비용',
        defaultOpen: false,
        blocks: [
          {
            strong: '법원 서류',
            lines: ['판결문, 구속영장(재소·출소증명서), 공소장(담보에 따라 약식기소 제외 등)'],
          },
          {
            strong: '비용 증빙',
            lines: ['선임한 변호사가 발행한 세금계산서'],
          },
        ],
      },
      {
        title: '면허정지·취소',
        defaultOpen: false,
        blocks: [
          { strong: '운전경력증명서', lines: ['경찰서'] },
          {
            strong: '면허정지',
            lines: [
              '운전면허 정지처분 결정통지서(교육수료 후) 또는 면허정지 행정처분 확인서',
            ],
          },
          {
            strong: '면허취소',
            lines: ['운전면허 취소처분 결정통지서 또는 면허취소 확인원'],
          },
        ],
      },
      {
        title: '교통사고처리지원금',
        defaultOpen: false,
        blocks: [
          {
            strong: '피해자 진단서, 공소장',
            lines: ['경찰서/법원'],
          },
          {
            strong: '합의 관련',
            lines: [
              '경찰서에 제출된 형사합의서(합의금액 명시), 형사합의금 입금 내역',
            ],
          },
          {
            strong: '미합의 시',
            lines: ['피해자 공탁금 출금 확인서 및 공탁서', '법원'],
          },
        ],
      },
      {
        title: '자동차보험과 연계(해당 시)',
        defaultOpen: false,
        blocks: [
          {
            strong: '자동차보험 보험금지급결의서',
            lines: ['보험사'],
          },
        ],
      },
      {
        title: '할증지원금·자동차 부상치료비',
        defaultOpen: false,
        blocks: [
          { strong: '사고증명서', lines: [] },
          {
            strong: '자동차보험에서 보상받은 경우',
            lines: ['해당 자동차보험 보상처리확인서'],
          },
          {
            strong: '보상받지 못한 경우(예시)',
            lines: [
              '(1) 자동차사고 입증: 교통사고사실확인원, 차량피해 견적서·사진 등',
              '(2) 상해등급 판단: 진단서 등',
            ],
          },
        ],
      },
      {
        title: '자동차사고 성형수술비',
        defaultOpen: false,
        blocks: [
          {
            strong: '진단서 또는 소견서',
            lines: ['진단명, 성형수술 부위와 크기, 수술내용 포함'],
          },
        ],
      },
      {
        title: '상해·치료비 담보(가입 상품에 포함된 경우)',
        defaultOpen: false,
        blocks: [
          {
            strong: '의료 서류',
            lines: [
              '진단서, 진료비세부(산정)내역서, 영수증·처방전, 입퇴원확인서(해당 시)',
              '자동차보험 대인·자동차상해 등과 중복·조정 여부는 약관·보험사 심사에 따릅니다.',
            ],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '자동차보험',
    panels: [
      {
        title: '공통·접수',
        defaultOpen: true,
        blocks: [
          {
            strong: '보험사 청구 양식',
            lines: [
              '보험금청구서, 개인(신용)정보 처리 동의서 — 담보(보장) 유형별로 보험사가 지정한 서식',
              '운전자 확인: 운전면허증 사본, 차량 운행·사고 경위 진술',
            ],
          },
          {
            strong: '사고 사실 확인',
            lines: [
              '교통사고사실확인원(경찰), 사고증명서·접수증 등 — 사고 유형·신고 여부에 따라',
              '현장 사진, 블랙박스 영상(보험사·조사 요청 시)',
            ],
          },
        ],
      },
      {
        title: '대물·자기차량 손해',
        defaultOpen: true,
        blocks: [
          {
            strong: '수리·손해액 입증',
            lines: [
              '견적서, 수리(공임·부품) 명세서, 수리비 영수증',
              '전손·분손·감가 등은 보험사 산정에 따라 추가 서류(감정평가 등) 요청될 수 있음',
            ],
          },
        ],
      },
      {
        title: '대인·부상(치료비)',
        defaultOpen: false,
        blocks: [
          {
            strong: '치료·진단',
            lines: [
              '진단서, 진료비세부(산정)내역서, 영수증·처방전',
              '입원 시 입퇴원확인서, 수술 시 수술확인서(해당 시)',
            ],
          },
          {
            strong: '합의·형사 등(해당 시)',
            lines: [
              '합의서·일부합의 확인서, 형사처분 결과 통지(보험사·사건에 따라)',
            ],
          },
        ],
      },
      {
        title: '대인배상(일상생활·시설 등)',
        defaultOpen: false,
        blocks: [
          {
            strong: '손해 입증',
            lines: [
              '피해 내용·금액을 확인할 수 있는 견적서·영수증·사진, 피해자 연락처 등',
              '보험사 조사·합의 절차에 맞춰 추가 서류를 제출합니다.',
            ],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '재물보험',
    panels: [
      {
        title: '공통',
        defaultOpen: true,
        blocks: [
          {
            strong: '사고 유형별',
            lines: [
              '화재: 화재증명원',
              '도난: 도난신고접수확인원',
              '기타: 사고증명서',
            ],
          },
          {
            strong: '청구 기본',
            lines: [
              '보험금청구서(계좌번호 포함), 개인(신용)정보처리동의서',
              '청구인 신분증(사업자등록증) 사본',
            ],
          },
          {
            strong: '대리인 청구 시',
            lines: [
              '위임장, 청구권자 인감증명서(또는 본인서명사실확인서), 청구권자 개인(신용)정보처리동의서',
            ],
          },
          {
            strong: '안내',
            lines: ['사고에 따라 담당자가 추가·원본 서류를 요구할 수 있습니다.'],
          },
        ],
      },
      {
        title: '건물',
        defaultOpen: false,
        blocks: [
          {
            strong: '건물등기부등본, 건축물관리대장',
            lines: ['등기소'],
          },
          { strong: '수리비 견적서·영수증', lines: [] },
          { strong: '임대차계약서 사본', lines: [] },
        ],
      },
      {
        title: '기계·비품',
        defaultOpen: false,
        blocks: [
          {
            strong: '명세·구입·수리',
            lines: [
              '기계기구 명세서, 구입영수증, 신품가격 견적서',
              '수리비 견적서·영수증, 감정평가서, 리스계약서(리스물건)',
            ],
          },
        ],
      },
      {
        title: '동산',
        defaultOpen: false,
        blocks: [
          {
            strong: '재고·손해·거래',
            lines: [
              '재고 및 손해명세서, 재고장부, 원가계산서, 거래명세서',
              '임가공계약서·작업지시서, 수불대장',
            ],
          },
        ],
      },
      {
        title: '가재도구',
        defaultOpen: false,
        blocks: [
          {
            strong: '가재도구명세서(구입연월일 명기)',
            lines: ['신품가격 견적서(구입처), 수리비 견적서·영수증'],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '배상책임보험',
    panels: [
      {
        title: '피보험자(고객)가 준비하는 서류',
        defaultOpen: true,
        blocks: [
          {
            strong: '신원·사고',
            lines: [
              '사업자등록증 사본',
              '사고경위서(6하 원칙에 의거하여 작성)',
              '사고현장 사진, 사고보증서(교통사고사실확인원 등)',
              '합의서, 인감증명서(또는 본인서명사실확인서)',
              '보험금 청구서(송금요청서)',
            ],
          },
        ],
      },
      {
        title: '손해액 평가 서류',
        defaultOpen: false,
        blocks: [
          {
            strong: '목록·비용',
            lines: [
              '손해품목 명세서, 견적서·영수증(세금계산서), 피해품 사진',
              '차량등록증 사본, 치료비 영수증',
            ],
          },
          {
            strong: '소득·사망 등',
            lines: [
              '의사의 진단서(소견서), 후유장해진단서(해당 시)',
              '근로계약서·재직증명서, 임금대장, 소득세 납세증명',
              '사망진단서 또는 사체검안서(사망한 경우)',
            ],
          },
        ],
      },
      {
        title: '피해자 신분 확인',
        defaultOpen: false,
        blocks: [
          {
            strong: '신분·목적물',
            lines: [
              '피해자 신분증(사업자등록증) 사본',
              '건물: 건물등기부등본 / 차량: 차량등록증 사본 / 중기: 건설기계등록증 사본',
            ],
          },
        ],
      },
      {
        title: '피보험자(고객) 신분 확인',
        defaultOpen: false,
        blocks: [
          {
            strong: '보험금청구서',
            lines: ['개인(신용)정보처리동의서·계좌번호 포함'],
          },
          { strong: '청구인 신분증 사본', lines: [] },
        ],
      },
      {
        title: '홀인원·알바트로스 등(가입 특약 해당 시)',
        defaultOpen: false,
        blocks: [
          {
            strong: '고객 준비 서류 예시',
            lines: [
              '보험금 청구서(송금요청서), 사고경위서(6하 원칙)',
              '스코어 카드, 동반자 확인서(동반 경기자·캐디·골프장 책임자 등 공동서명·날인)',
              '기념품·만찬·축하 라운드 등 비용 지출 명세(선불카드·상품권 물품전표 제외 등)',
            ],
          },
        ],
      },
      {
        title: '마무리 안내',
        defaultOpen: false,
        blocks: [
          {
            strong: '대체·추가',
            lines: [
              '위 서류는 사고에 따라 다른 서류로 대체될 수 있습니다.',
              '기타 추가 서류는 계약·보상 상담 채널로 문의하세요.',
            ],
          },
        ],
      },
    ],
  },
  {
    sectionTitle: '구직급여·실업급여 지원',
    panels: [
      {
        title: '상해·질병 구직급여 지원금',
        defaultOpen: true,
        blocks: [
          { strong: '고용보험수급자격증', lines: [] },
          {
            strong: '실업급여지급결정통지서',
            lines: ['직업안정기관 장 발행'],
          },
          { strong: '병가신청서', lines: [] },
          {
            strong: '의사소견서',
            lines: ['상해·질병으로 고용업체 업무수행이 더 이상 불가하다는 취지'],
          },
        ],
      },
      {
        title: '구직급여일당·장기구직급여 지원금',
        defaultOpen: false,
        blocks: [
          { strong: '고용보험수급자격증', lines: [] },
          {
            strong: '실업급여지급결정통지서',
            lines: ['직업안정기관 장 발행'],
          },
        ],
      },
    ],
  },
]

/** 고객지원도구 청구 서류 체크: 일반 + 유형별 (선택 UI 순서) */
export const CLAIM_DOCUMENT_SECTIONS_ALL = [CLAIM_GENERAL_DOCUMENTS_SECTION, ...CLAIM_REQUIRED_DOCUMENTS_GUIDE]
